import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Quote, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { CITATION_STYLES } from "@/lib/citations";

export default function CiteThisEntry({ entry, pageUrl }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("chicago");
  const [copied, setCopied] = useState(false);

  const style = CITATION_STYLES.find((s) => s.key === active) || CITATION_STYLES[0];
  const text = style.format(entry, pageUrl);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${style.label} citation copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Copy citation failed", err);
      toast.error("Copy failed — select & copy manually.");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="cite-entry-btn"
        className="inline-flex items-center gap-2 underline underline-offset-4 hover:text-black"
      >
        <Quote className="w-4 h-4" /> Cite this entry
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="rounded-none max-w-2xl border-2 border-black p-0 gap-0 bg-white"
          data-testid="cite-dialog"
        >
          <DialogHeader className="px-6 py-4 border-b border-black/15">
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">
              Cite this entry
            </DialogTitle>
            <DialogDescription className="text-xs font-mono-plex uppercase tracking-wider text-[#555]">
              Pre-formatted reference strings — one click to copy.
            </DialogDescription>
          </DialogHeader>

          {/* Style tabs */}
          <div className="flex border-b border-black/15">
            {CITATION_STYLES.map((s) => (
              <button
                key={s.key}
                data-testid={`cite-tab-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`flex-1 px-4 py-3 font-mono-plex text-xs uppercase tracking-[0.2em] border-r last:border-r-0 border-black/10 transition ${
                  active === s.key
                    ? "bg-black text-white"
                    : "bg-white text-[#555] hover:bg-[#FAFAFA]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Citation body */}
          <div className="px-6 py-5 bg-[#F9F9F9] border-b border-black/15">
            <pre
              data-testid={`cite-output-${active}`}
              className="font-serif text-[15px] leading-[1.55] whitespace-pre-wrap break-words text-[#111] select-all"
            >
              {text}
            </pre>
          </div>

          <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-white">
            <p className="text-xs text-[#555] font-mono-plex uppercase tracking-wider">
              For research use — always verify with your style guide.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="rounded-none border-black/40 h-10"
                data-testid="cite-close-btn"
              >
                Close
              </Button>
              <Button
                onClick={copy}
                className="rounded-none bg-black text-white hover:bg-[#333] h-10"
                data-testid="cite-copy-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1.5" /> Copy {style.label}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
